import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { Card, EmptyState, FlowLayout, Icon, Skeleton } from '@/ds'
import { formatMoney, time, weekdayDayMonth } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { reservaService } from '@/services/reserva'
import type { ChangeResult } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { guestsText, readResult, TOTAL_CHANGE_STEPS, TYPE_BY_SLUG, useBookingStay, useLang, useReplaceStay, writeDraft, writeResult, useChangeExit } from './shared'

const HOME = '/'

/** Datos que muestra la confirmación según lo que ha cambiado. */
function Rows({ result }: { result: ChangeResult }) {
  const { t, lang } = useLang()
  const { stay } = result
  const when = (iso: string) => `${weekdayDayMonth(iso, lang)} · ${time(iso, lang)}`
  const rows: [string, string][] = []
  if (result.type === 'guests') rows.push([t('v2.reserva.datos.personas'), guestsText(stay.guests.adults, stay.guests.children, lang, t)])
  else if (result.type === 'unit') rows.push([t('v2.reserva.listo.alojamiento'), stay.unit.name])
  if (result.type !== 'guests') {
    rows.push([t('v2.reserva.datos.entrada'), when(stay.dates.checkIn)])
    rows.push([t('v2.reserva.datos.salida'), when(stay.dates.checkOut)])
  }
  if (result.paid) rows.push([t('v2.reserva.listo.pagadoAhora'), result.cardLast4 ? t('v2.reserva.listo.pagadoTarjeta', { amount: formatMoney(result.paid, lang), last4: result.cardLast4 }) : formatMoney(result.paid, lang)])
  if (result.refund) rows.push([t('v2.reserva.precio.teDevolvemos'), formatMoney(result.refund, lang)])
  return (
    <Card>
      <dl className="reserva__rows">
        {rows.map(([label, value]) => (
          <div key={label} className="reserva__row">
            <dt>{label}</dt>
            <dd className="tnum">{value}</dd>
          </div>
        ))}
      </dl>
      {result.receiptUrl && (
        <a className="pagos__chevron-link" href={result.receiptUrl} target="_blank" rel="noreferrer">
          {t('v2.reserva.listo.recibo')}
          <Icon name="chevron-right" size={20} />
        </a>
      )}
    </Card>
  )
}

/**
 * 8.5 · Paso 4 de un cambio: confirmación (Reserva.dc.html screen=done, §5e). El resultado viene del cambio aplicado
 * (E12 sin cobro, o el endpoint transaccional tras autorizar el banco) y la reserva de la app se sustituye por la
 * actualizada. "Qué se ha actualizado solo" solo con lo que diga el backend; el aviso a acompañantes está pospuesto.
 */
export function CambiarListo() {
  const { tipo = '' } = useParams()
  const type = TYPE_BY_SLUG[tipo]
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useBookingStay()
  const replaceStay = useReplaceStay()
  const { go } = useAppNavigate()
  const back = useChangeExit()
  const routeState = useLocation().state as { changeId?: string | null } | null
  const [stored] = useState(() => (type ? readResult(stay.guestCode, type) : null))
  const changeId = stored ? null : (routeState?.changeId ?? null)
  const [fetched, reload] = useAsync(`cambio:${stay.guestCode}:${changeId ?? 'ninguno'}`, (signal) => (changeId ? reservaService.getChangeResult(stay, changeId, apiLang, signal) : Promise.resolve(null)))
  const result = stored ?? (fetched.kind === 'ready' ? fetched.value : null)

  // La app pasa a usar la reserva ya cambiada (llave, fianza y Tu estancia se recalculan con ella).
  useEffect(() => {
    if (!result) return
    writeResult(stay.guestCode, result)
    writeDraft(null)
    if (result.stay.dates.checkIn !== stay.dates.checkIn || result.stay.dates.checkOut !== stay.dates.checkOut || result.stay.totalPrice?.amount !== stay.totalPrice?.amount || result.stay.guests.adults !== stay.guests.adults || result.stay.unit.name !== stay.unit.name) replaceStay(result.stay)
    // Solo al tener el resultado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  const lead = !result ? undefined : result.confirmationSentTo ? t(result.receiptUrl ? 'v2.reserva.listo.lead' : 'v2.reserva.listo.leadSinRecibo', { email: result.confirmationSentTo }) : t('v2.reserva.listo.leadSinEmail')

  return (
    <FlowLayout
      progress={{ step: TOTAL_CHANGE_STEPS, total: TOTAL_CHANGE_STEPS, backLabel: back.backLabel, onBack: () => back.exit(), saved: false }}
      title={result ? t('v2.reserva.listo.titulo') : t('v2.reserva.listo.sinCambioTitulo')}
      intro={lead}
      primary={result ? { label: t('v2.reserva.listo.volver'), onClick: () => go(HOME) } : undefined}
      secondary={back.origin === 'reserva' ? { label: t('v2.reserva.listo.verReserva'), onClick: () => back.exit() } : { label: back.returnLabel, onClick: () => back.exit() }}
    >
      {fetched.kind === 'loading' && !stored ? (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={96} />
          <Skeleton height={176} />
        </div>
      ) : fetched.kind === 'error' && !stored ? (
        <SectionError code={fetched.code} onRetry={reload} />
      ) : result ? (
        <>
          <div className="reserva__done" role="status">
            <Icon name="check-circle" size={64} strokeWidth={1.5} />
            <h2>{t(`v2.reserva.listo.hecho.${result.type}`)}</h2>
          </div>
          <Rows result={result} />
          {result.updated.length > 0 && (
            <div className="reserva__policy">
              <h3 className="reserva__policy-title">{t('v2.reserva.listo.actualizado')}</h3>
              <p className="reserva__policy-text">{result.updated.join(' ')}</p>
            </div>
          )}
        </>
      ) : (
        <EmptyState icon="calendar" title={t('v2.reserva.listo.sinCambio')} headingLevel={2}>
          {t('v2.reserva.listo.sinCambioTexto', { lang })}
        </EmptyState>
      )}
    </FlowLayout>
  )
}
